import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = axios.create({
  baseURL: `${BACKEND_URL}/api`,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
API.interceptors.request.use((config) => {
  const token = localStorage.getItem('mercawatch_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors
API.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('mercawatch_token');
      localStorage.removeItem('mercawatch_user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth
export const login = (email, password) => API.post('/auth/login', { email, password });
export const registerAdmin = (data) => API.post('/auth/register-admin', data);
export const getCurrentUser = () => API.get('/auth/me');

// Users
export const getUsers = (params) => API.get('/users', { params });
export const getUser = (id) => API.get(`/users/${id}`);
export const createUser = (data) => API.post('/users', data);
export const updateUser = (id, data) => API.put(`/users/${id}`, data);
export const deleteUser = (id) => API.delete(`/users/${id}`);
export const getSupervisors = () => API.get('/users/supervisors/list');
export const getMercaderistas = (supervisorId) => API.get('/users/mercaderistas/list', { params: { supervisor_id: supervisorId } });

// Chains (Cadenas de supermercados)
export const getChains = (params) => API.get('/chains', { params });
export const getActiveChains = () => API.get('/chains/active');
export const getChain = (id) => API.get(`/chains/${id}`);
export const createChain = (data) => API.post('/chains', data);
export const updateChain = (id, data) => API.put(`/chains/${id}`, data);
export const deleteChain = (id) => API.delete(`/chains/${id}`);

// Sections (Secciones)
export const getSections = (params) => API.get('/sections', { params });
export const getActiveSections = () => API.get('/sections/active');
export const getSection = (id) => API.get(`/sections/${id}`);
export const createSection = (data) => API.post('/sections', data);
export const updateSection = (id, data) => API.put(`/sections/${id}`, data);
export const deleteSection = (id) => API.delete(`/sections/${id}`);

// Categories (Categorías)
export const getCategories = (params) => API.get('/sections/categories/list', { params });
export const getActiveCategories = () => API.get('/sections/categories/active');
export const getCategory = (id) => API.get(`/sections/categories/${id}`);
export const createCategory = (data) => API.post('/sections/categories', data);
export const updateCategory = (id, data) => API.put(`/sections/categories/${id}`, data);
export const deleteCategory = (id) => API.delete(`/sections/categories/${id}`);

// Stores
export const getStores = (params) => API.get('/stores', { params });
export const getStore = (id) => API.get(`/stores/${id}`);
export const createStore = (data) => API.post('/stores', data);
export const updateStore = (id, data) => API.put(`/stores/${id}`, data);
export const deleteStore = (id) => API.delete(`/stores/${id}`);
export const getStoreSkus = (storeId) => API.get(`/stores/${storeId}/skus`);
export const getStoreSectionsWithSkus = (storeId) => API.get(`/stores/${storeId}/sections-with-skus`);

// Store Sections
export const getStoreSections = (storeId) => API.get(`/store-config/stores/${storeId}/sections`);
export const addStoreSection = (storeId, data) => API.post(`/store-config/stores/${storeId}/sections`, data);
export const updateStoreSection = (id, data) => API.put(`/store-config/store-sections/${id}`, data);
export const deleteStoreSection = (id) => API.delete(`/store-config/store-sections/${id}`);

// Store Section SKUs
export const getStoreSectionSkus = (storeId, sectionId) => API.get(`/store-config/stores/${storeId}/sections/${sectionId}/skus`);
export const addStoreSectionSku = (data) => API.post('/store-config/store-section-skus', data);
export const updateStoreSectionSku = (id, data) => API.put(`/store-config/store-section-skus/${id}`, data);
export const deleteStoreSectionSku = (id) => API.delete(`/store-config/store-section-skus/${id}`);

// SKU Prices
export const getSkuPrices = (skuId) => API.get(`/store-config/skus/${skuId}/prices`);
export const getChainPrices = (chainId) => API.get(`/store-config/chains/${chainId}/prices`);
export const createSkuPrice = (data) => API.post('/store-config/sku-prices', data);
export const updateSkuPrice = (id, data) => API.put(`/store-config/sku-prices/${id}`, data);
export const deleteSkuPrice = (id) => API.delete(`/store-config/sku-prices/${id}`);

// SKUs
export const getSkus = (params) => API.get('/skus', { params });
export const getSku = (id) => API.get(`/skus/${id}`);
export const createSku = (data) => API.post('/skus', data);
export const updateSku = (id, data) => API.put(`/skus/${id}`, data);
export const deleteSku = (id) => API.delete(`/skus/${id}`);
export const getBrands = () => API.get('/skus/brands');
export const createStoreSku = (data) => API.post('/skus/store-skus', data);
export const updateStoreSku = (id, data) => API.put(`/skus/store-skus/${id}`, data);
export const deleteStoreSku = (id) => API.delete(`/skus/store-skus/${id}`);

// Route Templates (Plantillas de Ruta Reutilizables)
export const getRouteTemplates = (params) => API.get('/route-templates', { params });
export const getRouteTemplate = (id) => API.get(`/route-templates/${id}`);
export const createRouteTemplate = (data) => API.post('/route-templates', data);
export const updateRouteTemplate = (id, data) => API.put(`/route-templates/${id}`, data);
export const deleteRouteTemplate = (id) => API.delete(`/route-templates/${id}`);
export const duplicateRouteTemplate = (id, newName = null) => 
  API.post(`/route-templates/${id}/duplicate`, null, { params: newName ? { new_name: newName } : {} });
export const addStoreToTemplate = (templateId, storeId, order = 0) => 
  API.post(`/route-templates/${templateId}/stores`, null, { params: { store_id: storeId, route_order: order } });
export const addStoresToTemplateBulk = (templateId, storeIds) => 
  API.post(`/route-templates/${templateId}/stores/bulk`, storeIds);
export const updateTemplateStoreOrder = (templateId, storeEntryId, newOrder) => 
  API.put(`/route-templates/${templateId}/stores/${storeEntryId}/order`, null, { params: { new_order: newOrder } });
export const removeStoreFromTemplate = (templateId, storeEntryId) => 
  API.delete(`/route-templates/${templateId}/stores/${storeEntryId}`);
export const getTemplateUsers = (templateId) => API.get(`/route-templates/${templateId}/users`);

// Assignments (Asignacion de Múltiples Rutas a Mercaderistas)
export const getAssignments = (params) => API.get('/assignments', { params });
export const getUserRoutes = (userId) => API.get(`/assignments/by-user/${userId}`);
export const createAssignment = (data) => API.post('/assignments', data);
export const updateAssignmentOrder = (id, newOrder) => API.put(`/assignments/${id}/order`, null, { params: { new_order: newOrder } });
export const deleteAssignment = (id) => API.delete(`/assignments/${id}`);
export const deleteUserAssignments = (userId) => API.delete(`/assignments/by-user/${userId}`);
export const getMercaderistasWithRoutes = () => API.get('/assignments/mercaderistas/active');
export const getUserProgress = (userId) => API.get(`/assignments/progress/${userId}`);
export const getNextStore = (userId) => API.get(`/assignments/next-store/${userId}`);
export const getPendingStores = (userId) => API.get(`/assignments/pending-stores/${userId}`);
export const downloadAssignmentsTemplate = () => API.get('/assignments/template', { responseType: 'blob' });
export const uploadAssignmentsFile = (file) => {
  const formData = new FormData();
  formData.append('file', file);
  return API.post('/assignments/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
};

// Visits
export const getVisits = (params) => API.get('/visits', { params });
export const getActiveVisits = () => API.get('/visits/active');
export const getVisit = (id) => API.get(`/visits/${id}`);
export const getVisitEvidence = (id) => API.get(`/visits/${id}/evidence`);
export const getVisitInventory = (id) => API.get(`/visits/${id}/inventory`);
export const exportVisits = (params) => API.get('/visits/export/excel', { params, responseType: 'blob' });
export const getVisitsSummary = (params) => API.get('/visits/stats/summary', { params });

// Alerts
export const getAlerts = (params) => API.get('/alerts', { params });
export const getPendingAlerts = () => API.get('/alerts/pending');
export const getAlertStats = () => API.get('/alerts/stats');
export const acknowledgeAlert = (id) => API.post(`/alerts/${id}/acknowledge`);
export const acknowledgeBulkAlerts = (ids) => API.post('/alerts/acknowledge-bulk', ids);
export const getAlert = (id) => API.get(`/alerts/${id}`);

// Bulk Upload - Download Templates
export const downloadChainsTemplate = () => API.get('/bulk/chains/template', { responseType: 'blob' });
export const downloadSectionsTemplate = () => API.get('/bulk/sections/template', { responseType: 'blob' });
export const downloadCategoriesTemplate = () => API.get('/bulk/categories/template', { responseType: 'blob' });
export const downloadSkusTemplate = () => API.get('/bulk/skus/template', { responseType: 'blob' });
export const exportStoresCatalog = () => API.get('/stores/export', { responseType: 'blob' });
export const downloadStoresTemplate = () => API.get('/bulk/stores/template', { responseType: 'blob' });
export const downloadSkuPricesTemplate = () => API.get('/bulk/sku-prices/template', { responseType: 'blob' });
export const downloadStoreSectionSkusTemplate = () => API.get('/bulk/store-section-skus/template', { responseType: 'blob' });

// Bulk Upload - Upload Files
export const uploadChainsFile = (file) => {
  const formData = new FormData();
  formData.append('file', file);
  return API.post('/bulk/chains/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
};
export const uploadSectionsFile = (file) => {
  const formData = new FormData();
  formData.append('file', file);
  return API.post('/bulk/sections/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
};
export const uploadCategoriesFile = (file) => {
  const formData = new FormData();
  formData.append('file', file);
  return API.post('/bulk/categories/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
};
export const uploadSkusFile = (file) => {
  const formData = new FormData();
  formData.append('file', file);
  return API.post('/bulk/skus/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
};
export const uploadStoresFile = (file) => {
  const formData = new FormData();
  formData.append('file', file);
  return API.post('/bulk/stores/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
};
export const uploadSkuPricesFile = (file) => {
  const formData = new FormData();
  formData.append('file', file);
  return API.post('/bulk/sku-prices/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
};
export const uploadStoreSectionSkusFile = (file) => {
  const formData = new FormData();
  formData.append('file', file);
  return API.post('/bulk/store-section-skus/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
};

// ===== AI Agent: Vision, Deviations, Pursuit =====
export const visionStatus = () => API.get('/vision/status');
export const analyzeShelf = (data) => API.post('/vision/analyze-shelf', data);
export const listAnalyses = (params) => API.get('/vision/analyses', { params });
export const getAnalysis = (id) => API.get(`/vision/analyses/${id}`);

export const createDeviation = (data) => API.post('/deviations', data);
export const listDeviations = (params) => API.get('/deviations', { params });
export const listPendingDeviations = () => API.get('/deviations/pending');
export const supervisorDecideDeviation = (id, data) => API.post(`/deviations/${id}/supervisor`, data);
export const officeDecideDeviation = (id, data) => API.post(`/deviations/${id}/office`, data);

export const runPursuitNow = () => API.post('/pursuit/run-now');
export const getPursuitLogs = (params) => API.get('/pursuit/logs', { params });
export const getStoreTimeEstimate = (storeId) => API.get(`/pursuit/store-time/${storeId}`);

// Analytics
export const getShareOfShelf = (params) => API.get('/analytics/share-of-shelf', { params });
export const getComplianceSummary = (params) => API.get('/analytics/compliance-summary', { params });

export default API;
